import { useRef, useState, type FormEvent } from "react";
import { GOOGLE_CLIENT_ID, WORKER_HTTP_BASE } from "../config.ts";
import "./onboarding.css";

/**
 * Mov onboarding: cinematic hero → REAL "Sign up with Google" (name/email/
 * profile only) → create password → welcome. Google's genuine popup, no fakes.
 */

type Step = "hero" | "password" | "welcome";
export interface OnboardProfile {
  name: string;
  email: string;
  picture?: string;
  sub?: string;
}

export function Onboarding({ onDone }: { onDone: (p: OnboardProfile) => void }) {
  const [step, setStep] = useState<Step>("hero");
  const [profile, setProfile] = useState<OnboardProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tokenClient = useRef<ReturnType<NonNullable<Window["google"]>["accounts"]["oauth2"]["initTokenClient"]> | null>(
    null,
  );

  function ensureGoogle(): boolean {
    if (tokenClient.current) return true;
    const g = window.google;
    if (!g?.accounts?.oauth2) return false;
    tokenClient.current = g.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: "openid email profile",
      callback: async (resp) => {
        if (resp.error || !resp.access_token) {
          setBusy(false);
          setError("Google sign-in was cancelled.");
          return;
        }
        try {
          const r = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
            headers: { Authorization: "Bearer " + resp.access_token },
          });
          const p = await r.json();
          setProfile({ name: p.name || p.given_name || "there", email: p.email || "", picture: p.picture, sub: p.sub });
          setBusy(false);
          setStep("password");
        } catch {
          setBusy(false);
          setError("Couldn't read your Google profile. Try again.");
        }
      },
    });
    return true;
  }

  const signUpWithGoogle = () => {
    setError(null);
    if (!ensureGoogle()) {
      setError("Google is still loading — give it a second and try again.");
      return;
    }
    setBusy(true);
    tokenClient.current!.requestAccessToken({ prompt: "consent" });
  };

  async function saveSignup(password: string): Promise<string | null> {
    if (!profile) return "No profile yet.";
    try {
      const r = await fetch(`${WORKER_HTTP_BASE}/api/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          googleSub: profile.sub,
          email: profile.email,
          name: profile.name,
          picture: profile.picture,
          password,
        }),
      });
      const d = await r.json();
      if (!r.ok || d.error) return d.error || "Sign-up failed.";
      return null;
    } catch {
      return "Couldn't reach the server. Is the worker running on :8787?";
    }
  }

  return (
    <div className={`ob ob-step-${step}`}>
      <div className="ob-bg" aria-hidden />
      <div className="ob-scrim" aria-hidden />

      <header className="ob-top">
        <div className="ob-word">MOV</div>
      </header>

      <main className="ob-main">
        {step === "hero" && (
          <section className="ob-hero">
            <div className="ob-eyebrow">A private cinema for two</div>
            <h1 className="ob-title">Movies are better watched together.</h1>
            <p className="ob-sub">Press play in perfect sync, from anywhere. One account, one link, one movie night.</p>
            <button className="ob-google ob-google-lg" onClick={signUpWithGoogle} disabled={busy}>
              <GoogleG />
              <span>{busy ? "Opening Google…" : "Sign up with Google"}</span>
            </button>
            {error && <p className="ob-error">{error}</p>}
          </section>
        )}

        {step === "password" && profile && (
          <PasswordStep profile={profile} error={error} setError={setError} onBack={() => setStep("hero")} onSubmit={saveSignup} onDone={() => setStep("welcome")} />
        )}

        {step === "welcome" && profile && (
          <section className="ob-card ob-welcome">
            {profile.picture ? (
              <img className="ob-avatar" src={profile.picture} alt="" referrerPolicy="no-referrer" />
            ) : (
              <div className="ob-avatar ob-avatar-fallback">{profile.name.charAt(0).toUpperCase()}</div>
            )}
            <h2 className="ob-card-title">Welcome, {profile.name.split(" ")[0]}</h2>
            <p className="ob-sub ob-sub-tight">{profile.email}</p>
            <button className="ob-btn ob-btn-block" onClick={() => onDone(profile)}>
              Enter Mov
            </button>
          </section>
        )}
      </main>
    </div>
  );
}

function PasswordStep({
  profile,
  error,
  setError,
  onBack,
  onSubmit,
  onDone,
}: {
  profile: OnboardProfile;
  error: string | null;
  setError: (s: string | null) => void;
  onBack: () => void;
  onSubmit: (password: string) => Promise<string | null>;
  onDone: () => void;
}) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (pw.length < 6) return setError("Use at least 6 characters.");
    if (pw !== pw2) return setError("Passwords don't match.");
    setError(null);
    setSaving(true);
    const err = await onSubmit(pw);
    setSaving(false);
    if (err) return setError(err);
    onDone();
  };
  return (
    <section className="ob-card">
      <div className="ob-eyebrow ob-center">Almost there, {profile.name.split(" ")[0]}</div>
      <h2 className="ob-card-title">Create a password</h2>
      <p className="ob-sub ob-sub-tight">for {profile.email}</p>
      <form className="ob-form" onSubmit={submit}>
        <Floating label="Create a password" type="password" value={pw} onChange={setPw} />
        <Floating label="Confirm password" type="password" value={pw2} onChange={setPw2} />
        <button className="ob-btn ob-btn-block" type="submit" disabled={saving}>
          {saving ? "Creating your account…" : "Continue"}
        </button>
      </form>
      {error && <p className="ob-error">{error}</p>}
      <button className="ob-link" onClick={onBack} disabled={saving}>
        ← Use a different account
      </button>
    </section>
  );
}

function Floating({ label, type, value, onChange }: { label: string; type: string; value: string; onChange: (v: string) => void }) {
  const [focus, setFocus] = useState(false);
  const active = focus || value.length > 0;
  return (
    <label className={`ob-float ${active ? "is-active" : ""}`}>
      <input type={type} value={value} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} onChange={(e) => onChange(e.target.value)} />
      <span className="ob-float-label">{label}</span>
    </label>
  );
}

function GoogleG() {
  return (
    <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.5 2.6 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.8 6.1C12.2 13.2 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.1 5.3-4.6 7l7.1 5.5c4.2-3.9 6.6-9.6 6.6-16z" />
      <path fill="#FBBC05" d="M10.3 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.8-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.7l7.8-6.1z" />
      <path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.1-5.5c-2 1.3-4.6 2.1-7.9 2.1-6.4 0-11.8-3.7-13.7-9l-7.8 6.1C6.4 42.6 14.6 48 24 48z" />
    </svg>
  );
}
